import React__default, { createContext, useRef, useCallback, useMemo } from 'react';
import { useIsomorphicLayoutEffect } from '@shopify/react-hooks';
import { TransitionGroup } from 'react-transition-group';
import { omitKeysFromObject } from '../../utilities/omit.mjs';

const TransitionContext = /*#__PURE__*/createContext(null);
const TRANSITION_CONTAINER_ACTION_PROPS = ['appear', 'enter', 'exit'];
function TransitionGroupManager({
  children
}) {
  return /*#__PURE__*/React__default.createElement(TransitionGroup, {
    childFactory: childFactoryCreator,
    component: null
  }, children);
}
function ChildFactory({
  child
}) {
  const {
    in: transitionIn,
    onExited,
    ...props
  } = child.props;
  const onExitedRef = useRef(onExited);
  const handleOnExited = useCallback(node => onExitedRef.current(node), []);
  const contextValue = useMemo(() => ({
    // TODO: Once we have had enough time to tophat this in production,
    // we may want to delay updating the `in` context for a certain timeout.
    // This is only necessary if we notice stuttering on Panel animations.
    // https://github.com/Shopify/online-store-ui/issues/2417
    in: transitionIn,
    depth: 0,
    onExited: handleOnExited
  }), [transitionIn, handleOnExited]);
  useIsomorphicLayoutEffect(() => {
    onExitedRef.current = onExited;
  }, [onExited]);
  return /*#__PURE__*/React__default.createElement(TransitionContext.Provider, {
    key: child.key,
    value: contextValue
  }, /*#__PURE__*/React__default.createElement(child.type, {
    ...omitKeysFromObject(props, ...TRANSITION_CONTAINER_ACTION_PROPS),
    ref: child === null || child === void 0 ? void 0 : child.ref
  }));
}
const childFactoryCreator = child => /*#__PURE__*/React__default.createElement(ChildFactory, {
  key: child.key,
  child: child
});

export { TRANSITION_CONTAINER_ACTION_PROPS, TransitionContext, TransitionGroupManager };
